import type { LineSeriesOption, BarSeriesOption } from 'echarts'
import type { Series } from './lineChartResponse'
import { resolveSeriesType } from './seriesTypes'

export interface BaseSeriesContext {
  /** Position of this series among all series across all results */
  seriesIndex: number
  /** Names of all trendline series in the chart (for trendline colour cycling) */
  trendSeriesNames: string[]
  /** Whether to show data point symbols */
  showPoints: boolean
  /** Name to use when the series name is empty */
  fallbackName?: string
  /** True only for the very first series — receives the highlight mark area */
  isFirstSeries: boolean
  /** When set, data points inside this window are carved out (for the inset overlay) */
  insetWindow: { insetStart: number; insetEnd: number } | null
  /** Returns the highlight mark area config, or null when none is active */
  createHighlightMarkArea: () => LineSeriesOption['markArea'] | null
}

/**
 * Removes data points that fall strictly inside [insetStart, insetEnd).
 * Points at exactly insetEnd are kept (inclusive boundary).
 * Returns the original array unchanged when the region is zero-width.
 */
export function carveOutInsetRegion(
  data: [string, number][],
  insetStart: number,
  insetEnd: number,
): [string, number][] {
  if (insetStart === insetEnd) return data
  return data.filter(([ts]) => {
    const t = new Date(ts).getTime()
    return t < insetStart || t >= insetEnd
  })
}

/**
 * Transforms a raw API Series into an ECharts series option, applying:
 * - name resolution (fallback / undefined when absent)
 * - series-type colour and area styles
 * - symbol visibility
 * - bar-specific layout props
 * - highlight mark area (first series only)
 * - inset-window data carving
 */
export function buildBaseSeriesEntry(
  s: Series,
  context: BaseSeriesContext,
): LineSeriesOption | BarSeriesOption {
  const { seriesIndex, trendSeriesNames, showPoints, fallbackName, isFirstSeries, insetWindow, createHighlightMarkArea } = context

  const seriesType = resolveSeriesType(s.name)
  const trendlineIndex = trendSeriesNames.indexOf(s.name)
  const styleOverrides = seriesType.resolveStyle({ seriesIndex, trendlineIndex })

  const entry = {
    ...s,
    name: s.name || fallbackName || undefined,
    animation: false,
    showSymbol: showPoints && !seriesType.symbolAlwaysHidden,
    symbolSize: 10,
    areaStyle: seriesType.resolveAreaStyle(),
    lineStyle: { ...s.lineStyle, ...styleOverrides.lineStyle },
    itemStyle: styleOverrides.itemStyle,
  }

  const result = entry as LineSeriesOption

  if (s.type === 'bar') {
    (result as BarSeriesOption).barCategoryGap = '30%';
    (result as BarSeriesOption).barMaxWidth = '80%'
  }

  if (isFirstSeries) {
    const markArea = createHighlightMarkArea()
    if (markArea) result.markArea = markArea
  }

  if (insetWindow) {
    const { insetStart, insetEnd } = insetWindow
    result.data = carveOutInsetRegion(
      s.data as [string, number][],
      insetStart,
      insetEnd,
    ) as LineSeriesOption['data']
  }

  return result
}
